'use client';

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type RefObject,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import type { SCAPIShopperStoresSchemas } from 'src/types';

type Store = SCAPIShopperStoresSchemas['Store'];

interface EventCourseStoreInfoContextValue {
    /** Deferred store linked to the product via `c_eventStoreId`. */
    eventStore: Promise<Store | undefined>;
    isDrawerOpen: boolean;
    /** Pass the activating control so focus can be returned to it when the drawer closes. */
    openDrawer: (opener?: HTMLElement | null) => void;
    /** Control that opened the drawer; the drawer is opened programmatically, so Radix cannot restore focus itself. */
    openerRef: RefObject<HTMLElement | null>;
    closeDrawer: () => void;
    setDrawerOpen: (open: boolean) => void;
}

const EventCourseStoreInfoContext = createContext<EventCourseStoreInfoContextValue | null>(null);

interface EventCourseStoreInfoProviderProps extends PropsWithChildren {
    eventStore: Promise<Store | undefined>;
}

/**
 * Shares the deferred event store and the "Info e contatti" drawer open state
 * between the booking box CTA (in the hero) and the drawer mounted at the view
 * root, mirroring the shared PDP provider pattern used by the event/course view.
 */
export function EventCourseStoreInfoProvider({
    eventStore,
    children,
}: EventCourseStoreInfoProviderProps): ReactElement {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const openerRef = useRef<HTMLElement | null>(null);

    const openDrawer = useCallback((opener?: HTMLElement | null) => {
        openerRef.current = opener ?? null;
        setIsDrawerOpen(true);
    }, []);
    const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

    const value = useMemo(
        () => ({
            eventStore,
            isDrawerOpen,
            openDrawer,
            openerRef,
            closeDrawer,
            setDrawerOpen: setIsDrawerOpen,
        }),
        [eventStore, isDrawerOpen, openDrawer, closeDrawer]
    );

    return <EventCourseStoreInfoContext.Provider value={value}>{children}</EventCourseStoreInfoContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEventCourseStoreInfo(): EventCourseStoreInfoContextValue {
    const context = useContext(EventCourseStoreInfoContext);
    if (!context) {
        throw new Error('useEventCourseStoreInfo must be used within EventCourseStoreInfoProvider');
    }
    return context;
}
